import React from "react";
import {
  Box,
  Chip,
  Typography,
  useTheme,
  Divider,
  Button,
} from "@mui/material";
import { FaTimes } from "react-icons/fa";
import dayjs, { Dayjs } from "dayjs";
import { useLazyGetRoleListQuery } from "../../Services/RolesService";
import Utils from "../../Helpers/Utils";
import { formatDisplayDate } from "../../Helpers/Utils/dateRange";

// Generic filter interface that can accommodate different filter types
interface FilterValue {
  [key: string]: any;
}

interface AppliedFilterProps {
  filters: FilterValue;
  onClearFilter: (filterKey: string) => void;
  onClearAll: () => void;
  filterLabels?: { [key: string]: string };
  customFormatters?: { [key: string]: (value: any) => string };
}

const AppliedFilter: React.FC<AppliedFilterProps> = ({
  filters,
  onClearFilter,
  onClearAll,
  filterLabels = {},
  customFormatters = {},
}) => {
  const theme = useTheme();

  // Default filter labels
  const defaultLabels: { [key: string]: string } = {
    search: "Search",
    role: "Role",
    status: "Status",
    isActive: "Status",
    seniorityLevel: "Seniority Level",
    experienceMin: "Min Experience",
    experienceMax: "Max Experience",
    dateRange: "Date Range",
    paymentStatus: "Payment Status",
    amountRange: "Amount Range",
    quantityRange: "Quantity Range",
    ...filterLabels,
  };

  // Default value formatters
  const defaultFormatters: { [key: string]: (value: any) => string } = {
    isActive: (value: boolean | string) => {
      if (value === true || value === "true") return "Active";
      if (value === false || value === "false") return "Inactive";
      return String(value);
    },
    // All applied-filter dates render as YYYY-MM-DD for consistency across the panel.
    startDate: (value: string) => formatDisplayDate(value),
    endDate: (value: string) => formatDisplayDate(value),
    dateRange: (value: [Dayjs | null, Dayjs | null]) => {
      if (!value || !Array.isArray(value)) return "";
      const [start, end] = value;
      if (!start && !end) return "";
      const startStr = start ? formatDisplayDate(start) : "";
      const endStr = end ? formatDisplayDate(end) : "";
      if (startStr && endStr) return `${startStr} - ${endStr}`;
      if (startStr) return `From ${startStr}`;
      if (endStr) return `Until ${endStr}`;
      return "";
    },
    amountRange: (value: { min: number | null; max: number | null }) => {
      if (!value || typeof value !== "object") return "";
      const { min, max } = value;
      if (min !== null && max !== null) return `€${min} - €${max}`;
      if (min !== null) return `Min €${min}`;
      if (max !== null) return `Max €${max}`;
      return "";
    },
    quantityRange: (value: { min: number | null; max: number | null }) => {
      if (!value || typeof value !== "object") return "";
      const { min, max } = value;
      if (min !== null && max !== null) return `${min} - ${max}`;
      if (min !== null) return `Min ${min}`;
      if (max !== null) return `Max ${max}`;
      return "";
    },
    experienceMin: (value: string | number) => {
      if (!value) return "";
      return `${value} ${value == 1 ? "year" : "years"}`;
    },
    experienceMax: (value: string | number) => {
      if (!value) return "";
      return `${value} ${value == 1 ? "year" : "years"}`;
    },
    ...customFormatters,
  };

  // Function to check if a filter value is considered "active" (not empty/default)
  const isFilterActive = (key: string, value: any): boolean => {
    if (value === null || value === undefined || value === "") return false;

    // Handle boolean filters
    if (typeof value === "boolean") return true;

    // Handle array filters (like dateRange)
    if (Array.isArray(value)) {
      return value.some(
        (item) => item !== null && item !== undefined && item !== ""
      );
    }

    // Handle object filters (like amountRange, quantityRange)
    if (typeof value === "object" && value !== null) {
      return Object.values(value).some(
        (val) => val !== null && val !== undefined && val !== ""
      );
    }

    // Handle string/number filters
    return String(value).trim() !== "";
  };

  // Function to format filter value for display
  const formatFilterValue = (key: string, value: any): string => {
    if (defaultFormatters[key]) {
      return defaultFormatters[key](value);
    }

    // Default string formatting
    if (typeof value === "string") {
      return value;
    }

    return String(value);
  };

  // Get active filters
  const activeFilters = Object.entries(filters).filter(([key, value]) =>
    isFilterActive(key, value)
  );

  // Don't render if no active filters
  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "center",
          overflowX: "auto",
          maxWidth: 600,
          gap: 1,
          py: "6px",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#c1c1c1",
            borderRadius: "3px",
            "&:hover": {
              background: "#a8a8a8",
            },
          },
        }}
      >
        {/* chips here */}
        {activeFilters.map(([key, value]) => {
          const formattedValue = formatFilterValue(key, value);
          const label = defaultLabels[key] || key;

          if (!formattedValue) return null;

          const labelLength = label.length;
          const valueLength = formattedValue.length;
          const minWidth = Math.max(labelLength, valueLength) * 8 + 40; // Approximate character width + padding

          return (
            <Chip
              key={key}
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      position: "absolute",
                      top: "-8px",
                      left: "8px",
                      background: "#fff",
                      px: "4px",
                      lineHeight: 1,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.8rem", mr: 1.5 }}
                  >
                    {Utils.capitalizeFirstLetter(formattedValue)}
                  </Typography>
                </Box>
              }
              onDelete={() => onClearFilter(key)}
              size="small"
              sx={{
                m: 0,
                height: 32,
                position: "relative",
                border: "1px solid",
                borderColor: theme.palette.primary.main,
                borderRadius: "6px",
                padding: "4px 8px",
                minWidth: `${minWidth}px`,
                ".MuiChip-label": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "6px",
                  padding: 0,
                  width: "100%",
                },
              }}
            />
          );
        })}
      </Box>

      <Button
        variant="outlined"
        size="small"
        onClick={onClearAll}
        sx={{ flexShrink: 0 }}
      >
        Clear All
      </Button>
    </Box>
  );
};

export default AppliedFilter;
